import Image from 'next/image';
import { useMemo } from 'react';
import { NETWORKS } from 'src/common/types/network';

import { KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { maskAddressValue } from '../../../../common/utils/format';
import { Trigger } from '../../domain';
import { ArgumentDisplay } from './components/ArgumentDisplay';
import { CurrencyDisplay } from './components/CurrencyDisplay';

interface Props {
  trigger: Trigger;
}

export function MorphoBorrowerStrategyTriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const networkDisplay = useMemo(() => {
    const chainId = trigger.context?.market_chain_id;
    if (!chainId) return '-';

    const network = NETWORKS.find((n) => n.chainId === chainId);
    if (!network) return '-';

    const chainIconUrl = getChainIconUrl(chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={network.name} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{network.name.replace(/-/g, ' ')}</span>
      </div>
    );
  }, [trigger.context, getChainIconUrl]);

  const volumeCurrencyDisplay = useMemo(() => {
    if (!trigger.currencyVolumeExpression) return '-';

    const chainId = trigger.context?.market_chain_id;

    return <CurrencyDisplay value={trigger.currencyVolumeExpression} chainId={chainId} />;
  }, [trigger.currencyVolumeExpression, trigger.context]);

  const executedWithDisplay = useMemo(() => {
    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <Text as="span" variant="body-sm" color="muted">
          Snapshots of Morpho market positions for market ID:
        </Text>
        <ArgumentDisplay value={maskAddressValue(trigger.context?.market_address)} suffix="" />
      </div>
    );
  }, [trigger.context?.market_address]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed with" value={executedWithDisplay} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Volume currency" value={volumeCurrencyDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
